import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiFetch, apiPost, apiPut } from "@/lib/api";
import { companyKeys } from "@/lib/query";

interface Company {
  id: string;
  companyName: string;
  trade: string | null;
  tradeCategoryId: string | null;
  tradeCategoryName: string | null;
  contactPerson: string | null;
  email: string | null;
  phone: string | null;
  status: string | null;
  type: string | null;
  isInternal: boolean;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
}

export type { Company };

export function useCompanies(type?: string) {
  return useQuery({
    queryKey: [...companyKeys.all, type],
    queryFn: () => apiFetch<Company[]>(type ? `/companies?type=${type}` : "/companies"),
  });
}

export function useCompany(id: string) {
  return useQuery({
    queryKey: companyKeys.detail(id),
    queryFn: () => apiFetch<Company>(`/companies/${id}`),
    enabled: !!id,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Company>) => apiPost<{ data: Company }>("/companies", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companyKeys.all });
      toast.success("Company created");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<Company>) =>
      apiPut<{ data: Company }>(`/companies/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companyKeys.all });
      toast.success("Company updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/companies/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: companyKeys.all });
      toast.success("Company deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
